import {types, Instance, getRoot, IAnyStateTreeNode} from 'mobx-state-tree';
import {DocumentWithId, DocumentWithIdArrayType} from './Document';
import {SellerDto, SellerLocation} from '@joyrideautos/auction-core/src/dtos/SellerDto';
import {DocumentWithId as DocumentWithIdDto} from '@joyrideautos/auction-core/src/dtos/DocumentDto';
import {toJS} from 'mobx';

export enum FeeScheduleFeeTypeEnum {
    PERCENT = 'percent',
    STEP_FUNCTION = 'stepFunction',
}

export const GeoLocation = types.model('GeoLocation', {
    lat: types.number,
    lon: types.number,
});

export interface GeoLocationType extends Instance<typeof GeoLocation> {}

export const Location = types
    .model('Location', {
        id: types.union(types.number, types.string),
        addr1: types.maybe(types.string),
        addr2: types.maybe(types.string),
        city: types.maybe(types.string),
        description: types.maybe(types.string),
        state: types.maybe(types.string),
        zip: types.maybe(types.union(types.string, types.number)),
        phone: types.maybe(types.string),
        latLon: types.maybe(GeoLocation),
        pickupMessage: types.maybe(types.string),
    })
    .views((self) => ({
        get dto(): SellerLocation {
            return toJS(self) as SellerLocation;
        },
        get address() {
            return `${self.addr1 || ''} ${self.addr2 || ''} ${self.city || ''}, ${self.state || ''} ${self.zip || ''}`;
        },
    }));

export interface LocationType extends Instance<typeof Location> {}

export const StepFunctionFee = types.model('StepFunctionFee', {
    fee: types.maybe(types.number),
    salePrice: types.maybe(types.number),
});

export const FeeSchedule = types.model('FeeSchedule', {
    effectiveDate: types.maybe(types.string),
    feeType: types.maybe(types.enumeration([FeeScheduleFeeTypeEnum.PERCENT, FeeScheduleFeeTypeEnum.STEP_FUNCTION])),
    fixedFee: types.maybe(types.number),
    stepFunctionFee: types.maybe(types.array(StepFunctionFee)),
    percentFee: types.maybe(types.number),
    licensedBuyerExcludeFixedFee: types.maybe(types.boolean),
    licensedBuyerExcludeVariableFee: types.maybe(types.boolean),
});

export const FeeScheduleFromDto = types.model('FeeScheduleFromDto', {
    effectiveDate: types.maybe(types.string),
    fixedFee: types.maybe(types.number),
    stepFunctionFee: types.maybe(types.array(StepFunctionFee)),
    percentFee: types.maybe(types.number),
    licensedBuyerExcludeFixedFee: types.maybe(types.boolean),
    licensedBuyerExcludeVariableFee: types.maybe(types.boolean),
});

export const FeeScheduleItems = types.model('FeeScheduleItems', {
    items: types.array(FeeSchedule),
});

export const FeeScheduleItemsFromDto = types.model('FeeScheduleItemsFromDto', {
    items: types.array(FeeScheduleFromDto),
});

export const SellerSettings = types.model('SellerSettings', {
    createdDate: types.maybe(types.string),
    updatedDate: types.maybe(types.string),
    emailAddress: types.maybe(types.string),
    internalEmailAddress: types.maybe(types.string),
    phoneNumber: types.maybe(types.string),
    feeSchedule: types.maybe(FeeScheduleItems),
});

export const SellerSettingsFromDto = types.model('SellerSettingsFromDto', {
    feeSchedule: types.maybe(FeeScheduleItemsFromDto),
});

export const SellerCustomFieldModel = types.model('SellerCustomFieldModel', {
    key: types.string, // custom_field_key, auto-generated
    name: types.string,
    isVisibleToBuyers: types.boolean,
    createdAt: types.string, // ISO Date
    createdBy: types.string,
    editedAt: types.maybe(types.string), // ISO Date
    editedBy: types.maybe(types.string),
    deletedAt: types.maybe(types.string), // ISO Date
    deletedBy: types.maybe(types.string),
});

export interface SellerCustomFieldType extends Instance<typeof SellerCustomFieldModel> {}

export const PayInFullEnabledFor = types.model('PayInFullEnabledFor', {
    vip: types.maybe(types.boolean),
    trusted: types.maybe(types.boolean),
    regular: types.maybe(types.boolean),
});

export const Seller = types
    .model('Seller', {
        sellerId: types.identifier,
        name: types.string,
        email: types.maybe(types.string),
        regions: types.optional(types.map(types.boolean), {}),
        locations: types.optional(types.map(Location), {}),
        twitchChannel: types.maybe(types.string),
        mixerChannel: types.maybe(types.string),
        red5desktop: types.maybe(types.string),
        red5mobile: types.maybe(types.string),
        settings: types.maybe(SellerSettings),
        policy_docs: types.maybe(types.array(DocumentWithId)),
        internal_docs: types.maybe(types.array(DocumentWithId)),
        payment_info_docs: types.maybe(types.array(DocumentWithId)),
        googleFolderId: types.maybe(types.string),
        sellerNotesEnabled: types.maybe(types.boolean),
        isBuyNowEnabled: types.maybe(types.boolean),
        isReservePriceEnabled: types.maybe(types.boolean),
        integration: types.maybe(types.string),
        requiredNumberOfPhotos: types.maybe(types.number),
        customFields: types.map(SellerCustomFieldModel), // not to show in the UI. use sellersStore.getCustomFieldsForSeller(sellerId)
        payInFullEnabledFor: types.optional(PayInFullEnabledFor, {}),
        paymentDueWindowHours: types.maybeNull(types.number),
    })
    .views((self) => ({
        getPhone(locationId: string) {
            return self.settings && self.settings.phoneNumber
                ? self.settings.phoneNumber
                : this.getPhoneForLocation(locationId);
        },
        getEmail() {
            return self.settings && self.settings.emailAddress ? self.settings.emailAddress : self.email;
        },
        getLocation(locationId: string): LocationType | undefined {
            return self.locations.get(locationId);
        },
        getPhoneForLocation(locationId: string) {
            const location = this.getLocation(locationId);
            // TODO: format phone
            return location && location.phone;
        },
        getAddressForLocation(locationId: string) {
            return this.getLocation(locationId)?.addr1;
        },
        getCityForLocation(locationId: string) {
            const location = this.getLocation(locationId);
            return location && `${location.city}, ${location.state} ${location.zip}`;
        },
        get currentFeeSchedule() {
            const schedules = self.settings?.feeSchedule?.items;
            if (schedules) {
                const now = Date.now();
                const sorted = schedules
                    .map((schedule) => ({effectiveDate: Date.parse(schedule.effectiveDate!), schedule}))
                    .sort((a, b) => b.effectiveDate - a.effectiveDate);
                const found = sorted.find(({effectiveDate}) => effectiveDate < now);
                return found?.schedule;
            } else {
                return undefined;
            }
        },
        getStreamName(isMobile: boolean) {
            return isMobile ? self.red5mobile : self.red5desktop;
        },
    }))
    .actions((self) => ({
        updateName(value: string) {
            self.name = value;
        },
    }));

export interface SellerType extends Instance<typeof Seller> {}

export interface SellerSettingsType extends Instance<typeof SellerSettings> {}

export interface FeeScheduleType extends Instance<typeof FeeSchedule> {}

export interface StepFunctionFeeType extends Instance<typeof StepFunctionFee> {}

export interface SellerSettingsTypeFromDtoType extends Instance<typeof SellerSettingsFromDto> {}

export interface FeeScheduleFromDtoType extends Instance<typeof FeeScheduleFromDto> {}

export const SellerReference = function (rootStore?: any) {
    function getSellersStore(parent: any) {
        const root = rootStore || getRoot(parent);
        return root.sellersStore;
    }

    return types.reference(Seller, {
        get(sellerId: string, parent: IAnyStateTreeNode): any {
            return getSellersStore(parent).findSeller(sellerId);
        },
        set(value) {
            return value.sellerId;
        },
    });
};

export function fromDto(seller: SellerDto): SellerType {
    const toDocs = (docs?: DocumentWithIdDto[]): DocumentWithIdArrayType | undefined => {
        return docs?.filter(Boolean) as unknown as DocumentWithIdArrayType;
    };
    return {
        ...seller,
        policy_docs: toDocs(seller.policy_docs),
        internal_docs: toDocs(seller.internal_docs),
    } as unknown as SellerType;
}

export function mapFromDto(sellers: Record<string, SellerDto>): Record<string, SellerType> {
    return Object.keys(sellers).reduce<Record<string, SellerType>>((_sellers, key) => {
        // console.log('seller', key, sellers[key]);
        try {
            _sellers[key] = fromDto(sellers[key]);
        } catch (e: any) {
            console.log('failed to map seller', e.message);
        }
        return _sellers;
    }, {});
}
